import { BaseObject } from './BaseObject';
import { SVGRenderer } from '../core/SVGRenderer';
import { Point2D } from '../types';

export class Ray extends BaseObject {
  public origin: Point2D;
  public passPoint: Point2D;
  public lineElem: SVGLineElement | null = null;
  public drawProgress: number = 1.0;

  constructor(
    renderer: SVGRenderer,
    origin: Point2D,
    passPoint: Point2D,
    id?: string
  ) {
    super(renderer, id);
    this.origin = { ...origin };
    this.passPoint = { ...passPoint };
    this.color = renderer.config.lineColor;
    this.strokeWidth = 2.5;
    this.render();
  }

  public render(): SVGElement {
    const farPoint = this.calculateFarPoint();

    this.lineElem = this.renderer.createElement('line', {
      id: this.id,
      x1: this.origin.x,
      y1: this.origin.y,
      x2: this.origin.x + (farPoint.x - this.origin.x) * this.drawProgress,
      y2: this.origin.y + (farPoint.y - this.origin.y) * this.drawProgress,
      stroke: this.color,
      'stroke-width': this.strokeWidth,
      'stroke-linecap': 'round',
      'marker-end': 'url(#arrow-head)',
    });

    this.renderer.layers.objects.appendChild(this.lineElem);
    this.element = this.lineElem;
    return this.lineElem;
  }

  private calculateFarPoint(): Point2D {
    const dx = this.passPoint.x - this.origin.x;
    const dy = this.passPoint.y - this.origin.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return { x: this.origin.x + 100, y: this.origin.y };

    const uX = dx / len;
    const uY = dy / len;

    // Use parametric ray-boundary clipping to SVG viewBox (0..1000, 0..700)
    // with a small inset so the arrowhead stays visible
    const margin = 10;
    const minX = margin, maxX = 1000 - margin;
    const minY = margin, maxY = 700 - margin;

    let tMax = 5000; // large sentinel
    if (Math.abs(uX) > 1e-9) {
      const tx = uX > 0 ? (maxX - this.origin.x) / uX : (minX - this.origin.x) / uX;
      if (tx > 0) tMax = Math.min(tMax, tx);
    }
    if (Math.abs(uY) > 1e-9) {
      const ty = uY > 0 ? (maxY - this.origin.y) / uY : (minY - this.origin.y) / uY;
      if (ty > 0) tMax = Math.min(tMax, ty);
    }

    return {
      x: this.origin.x + uX * tMax,
      y: this.origin.y + uY * tMax,
    };
  }

  public setDrawProgress(progress: number): void {
    this.drawProgress = Math.max(0, Math.min(1, progress));
    this.update();
  }

  public update(): void {
    if (!this.lineElem) return;
    const farPoint = this.calculateFarPoint();

    const curX2 = this.origin.x + (farPoint.x - this.origin.x) * this.drawProgress;
    const curY2 = this.origin.y + (farPoint.y - this.origin.y) * this.drawProgress;

    this.lineElem.setAttribute('x1', String(this.origin.x));
    this.lineElem.setAttribute('y1', String(this.origin.y));
    this.lineElem.setAttribute('x2', String(curX2));
    this.lineElem.setAttribute('y2', String(curY2));
    this.lineElem.setAttribute('stroke', this.color);
  }
}
